/**
 * Reading a server-sent event stream over `fetch`.
 *
 * `fetch` rather than `EventSource`, which cannot carry an `Authorization`
 * header — every stream ODI serves is authenticated, so `EventSource` was
 * never an option. The global fetch wrapper attaches the bearer token, so a
 * caller here gets authentication for free.
 *
 * Written first for the Loki tail and now shared with the support chat: two
 * callers, one frame parser. The wire format is the same in both directions
 * of the codebase, and a second hand-rolled copy of `\n\n` splitting is
 * exactly the kind of thing that drifts.
 */

/** Split a buffer on frame boundaries, emitting each `data:` payload parsed
 *  as JSON, and return whatever tail did not form a whole frame yet. */
export function parseSseFrames<TEvent>(
	buffer: string,
	onEvent: (event: TEvent) => void
): string {
	let rest = buffer;
	while (true) {
		const idx = rest.indexOf("\n\n");
		if (idx === -1) break;
		const frame = rest.slice(0, idx);
		rest = rest.slice(idx + 2);

		if (!frame) continue;
		if (frame.startsWith(":")) continue; // comment / keep-alive

		const dataLines: string[] = [];
		for (const line of frame.split("\n")) {
			if (line.startsWith("data:")) {
				dataLines.push(line.slice(5).trimStart());
			}
		}
		if (dataLines.length === 0) continue;
		const dataStr = dataLines.join("\n");
		try {
			onEvent(JSON.parse(dataStr) as TEvent);
		} catch (e) {
			console.log("Failed to parse SSE data:", e, dataStr);
		}
	}
	return rest;
}

/** Read an SSE stream to completion, calling onEvent for each parsed frame.
 *  Returns when the server closes it, or throws AbortError when the caller does. */
export async function consumeSseStream<TEvent>(
	url: string,
	signal: AbortSignal,
	onEvent: (event: TEvent) => void
): Promise<void> {
	const res = await fetch(url, { method: "GET", signal });
	if (!res.ok || !res.body) {
		throw new Error(`Stream request failed: ${res.status} ${res.statusText}`);
	}
	const reader = res.body.getReader();
	const decoder = new TextDecoder("utf-8");
	let buffer = "";
	while (true) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });
		buffer = parseSseFrames(buffer, onEvent);
	}
	parseSseFrames(buffer + decoder.decode(), onEvent);
}
